// Whiteboard relay socket hook.
// KEEP IN SYNC with the dashboard original.
//
// Drawing traffic (draw ops / pointer / pdf page flips) is kept OUT of React
// state for performance: board op-logs live in a mutable ref and interested
// components subscribe to raw events. Everything else (participants,
// leaderboard, questions, session lifecycle) goes through a reducer.
import { useCallback, useEffect, useReducer, useRef } from "react";
import { WS_TYPES } from "../wsProtocol";

const initialState = {
    status: "connecting", // connecting | connected | reconnecting | ended | error
    errorCode: null,
    you: null,
    session: null,
    participants: [],
    leaderboard: [],
    allowStudentDraw: false,
    pdf: null,
    activeQuestions: [],
    teacherOnline: false,
    results: null, // teacher only, present after session:end
    lastStar: null, // {pid, name, stars, star_value, at}
    lastGrade: null, // student only {qid, score, at}
    widgets: { timer: null, calendar: null, bg: {}, notes: {} },
    timeOffset: 0, // serverNow - clientNow, keeps the shared timer in sync
    hands: [], // [{pid, name, at}]
    lastWheel: null, // {pid, name, durationMs, at}
    activity: null, // aggregated poll/wordcloud state
    ownVote: null, // student's own vote {key, correct} for the running activity
    actAck: null, // wordcloud-game feedback {correct, points, at}
    reveal: null, // answers the teacher chose to show everyone
    video: null, // synced YouTube state {videoId, playing, time, at}
    sharedFiles: [], // teacher -> students files
    inbox: null, // teacher only {questions, submissions}
    shownQuestion: null, // a student question displayed to the class
    lastBadge: null, // {pid, name, badge, at}
    badgeCatalog: {}, // badgeId -> {id, name, icon, color}; rows carry ids only
};

function upsertQuestion(list, incoming) {
    const index = list.findIndex((q) => q.qid === incoming.qid);
    if (index === -1) return [...list, incoming];
    return list.map((q, i) => (i === index ? { ...q, ...incoming } : q));
}

function reducer(state, action) {
    const msg = action.msg;
    switch (action.type) {
        case "status":
            return { ...state, status: action.status, errorCode: action.errorCode || state.errorCode };
        case "snapshot":
            return {
                ...state,
                status: "connected",
                you: msg.you,
                session: msg.session || state.session,
                participants: msg.participants,
                leaderboard: msg.leaderboard,
                allowStudentDraw: msg.allowStudentDraw,
                pdf: msg.pdf,
                widgets: msg.widgets
                    ? {
                        timer: msg.widgets.timer,
                        calendar: msg.widgets.calendar,
                        bg: msg.widgets.bg || {},
                        // must survive a reconnect: without it a student who
                        // refreshes mid-curtain sees the board the teacher hid
                        curtain: Boolean(msg.widgets.curtain),
                        // the lesson notes must come back after a refresh too
                        notes: msg.widgets.notes || {},
                    }
                    : state.widgets,
                timeOffset: msg.widgets && msg.widgets.serverNow ? msg.widgets.serverNow - Date.now() : state.timeOffset,
                hands: msg.hands || [],
                activity: msg.activity || null,
                ownVote: msg.ownVote !== undefined ? msg.ownVote : null,
                reveal: msg.reveal || null,
                video: msg.video || null,
                sharedFiles: msg.sharedFiles || [],
                inbox: msg.inbox || null,
                shownQuestion: msg.shownQuestion || null,
                activeQuestions: msg.activeQuestions,
                teacherOnline: msg.teacherOnline,
                badgeCatalog: msg.badgeCatalog || {},
            };
        case "event": {
            switch (msg.t) {
                case WS_TYPES.PARTICIPANT_JOIN: {
                    const joined = msg.participant;
                    const exists = state.participants.some((p) => p.pid === joined.pid);
                    // mirror into the leaderboard immediately: it is the list the
                    // panels actually render, and the relay's push is debounced
                    let leaderboard = state.leaderboard;
                    if (joined.role === "student") {
                        const onBoard = leaderboard.some((row) => row.pid === joined.pid);
                        leaderboard = onBoard
                            ? leaderboard.map((row) =>
                                row.pid === joined.pid ? { ...row, name: joined.name, online: true } : row
                            )
                            : [...leaderboard, { pid: joined.pid, name: joined.name, score: joined.score || 0, stars: joined.stars || 0, online: true }];
                    }
                    return {
                        ...state,
                        leaderboard,
                        participants: exists
                            ? state.participants.map((p) => (p.pid === joined.pid ? joined : p))
                            : [...state.participants, joined],
                    };
                }
                case WS_TYPES.PARTICIPANT_LEAVE:
                    return {
                        ...state,
                        participants: state.participants.map((p) =>
                            p.pid === msg.pid ? { ...p, online: false } : p
                        ),
                        leaderboard: state.leaderboard.map((row) =>
                            row.pid === msg.pid ? { ...row, online: false } : row
                        ),
                    };
                case WS_TYPES.LEADERBOARD:
                    return { ...state, leaderboard: msg.leaderboard };
                case WS_TYPES.PERM_DRAW:
                    return { ...state, allowStudentDraw: msg.enabled };
                case WS_TYPES.PDF_OPEN:
                    return { ...state, pdf: msg.pdf };
                case WS_TYPES.PDF_PAGE:
                    return state.pdf ? { ...state, pdf: { ...state.pdf, page: msg.page } } : state;
                case WS_TYPES.Q_PUSHED: {
                    const pushExtras = {
                        deadline: msg.deadline || null,
                        maxAttempts: msg.maxAttempts || 1,
                        exit: Boolean(msg.exit),
                    };
                    let questions = state.activeQuestions;
                    for (const q of msg.questions) {
                        const existing = questions.find((item) => item.qid === q.qid);
                        // a re-push reopens the question but must not reset an
                        // already-given answer (the relay keeps it)
                        questions = upsertQuestion(questions, existing
                            ? { qid: q.qid, question: q, closed: false, ...pushExtras }
                            : {
                                qid: q.qid,
                                question: q,
                                closed: false,
                                answered: false,
                                ownScore: null,
                                ownAttempts: 0,
                                answers: [],
                                correctKey: null,
                                ...pushExtras,
                            });
                    }
                    return {
                        ...state,
                        activeQuestions: questions,
                        timeOffset: msg.serverNow ? msg.serverNow - Date.now() : state.timeOffset,
                        lastPush: { at: Date.now(), exam: msg.exam },
                    };
                }
                case WS_TYPES.Q_CLOSED:
                    return {
                        ...state,
                        activeQuestions: state.activeQuestions.map((q) =>
                            q.qid === msg.qid ? { ...q, closed: true, correctKey: msg.correctKey } : q
                        ),
                    };
                case WS_TYPES.Q_ACK:
                    return {
                        ...state,
                        activeQuestions: state.activeQuestions.map((q) =>
                            q.qid === msg.qid
                                ? {
                                    ...q,
                                    answered: true,
                                    ownAttempts: msg.attempts || 1,
                                    maxAttempts: msg.maxAttempts || q.maxAttempts,
                                    ownScore: msg.score != null ? msg.score : q.ownScore,
                                    feedback: msg.feedback || q.feedback || null,
                                }
                                : q
                        ),
                    };
                case WS_TYPES.ACT_UPDATE: {
                    const isNewActivity =
                        msg.activity &&
                        (!state.activity || state.activity.question !== msg.activity.question || state.activity.kind !== msg.activity.kind);
                    return {
                        ...state,
                        activity: msg.activity,
                        ownVote: msg.activity === null || isNewActivity ? null : state.ownVote,
                        actAck: msg.activity === null || isNewActivity ? null : state.actAck,
                    };
                }
                case WS_TYPES.ACT_ACK:
                    return { ...state, actAck: { correct: msg.correct, points: msg.points, at: Date.now() } };
                case WS_TYPES.REVEAL_UPDATE:
                    return { ...state, reveal: msg.reveal };
                case WS_TYPES.YT_UPDATE:
                    return {
                        ...state,
                        video: msg.video,
                        timeOffset: msg.serverNow ? msg.serverNow - Date.now() : state.timeOffset,
                    };
                case WS_TYPES.FILES_UPDATE:
                    return { ...state, sharedFiles: msg.files || [] };
                case WS_TYPES.INBOX_UPDATE:
                    return { ...state, inbox: { questions: msg.questions || [], submissions: msg.submissions || [] } };
                case WS_TYPES.SQ_SHOWN:
                    return { ...state, shownQuestion: msg.shown };
                case WS_TYPES.BADGE_AWARDED:
                    return {
                        ...state,
                        lastBadge: { pid: msg.pid, name: msg.name, badge: msg.badge, at: Date.now() },
                        badgeCatalog: { ...state.badgeCatalog, [msg.badge.id]: msg.badge },
                        leaderboard: state.leaderboard.map((row) =>
                            row.pid === msg.pid
                                ? { ...row, badges: [...(row.badges || []), msg.badge.id], score: row.score + (msg.badge.bonus || 0) }
                                : row
                        ),
                    };
                case WS_TYPES.CURTAIN:
                    return { ...state, widgets: { ...state.widgets, curtain: msg.enabled } };
                case WS_TYPES.Q_ANSWERED: // teacher only
                    return {
                        ...state,
                        activeQuestions: state.activeQuestions.map((q) =>
                            q.qid === msg.qid
                                ? {
                                    ...q,
                                    answers: [
                                        ...(q.answers || []).filter((a) => a.pid !== msg.pid),
                                        {
                                            pid: msg.pid,
                                            name: msg.name,
                                            answer: msg.answer,
                                            score: msg.score,
                                            graded: Boolean(msg.graded),
                                        },
                                    ],
                                }
                                : q
                        ),
                    };
                case WS_TYPES.GRADED: // student only
                    return {
                        ...state,
                        lastGrade: { qid: msg.qid, score: msg.score, at: Date.now() },
                        activeQuestions: state.activeQuestions.map((q) =>
                            q.qid === msg.qid ? { ...q, ownScore: msg.score } : q
                        ),
                    };
                case WS_TYPES.STAR_AWARDED:
                    return {
                        ...state,
                        lastStar: { ...msg, at: Date.now() },
                        participants: state.participants.map((p) =>
                            p.pid === msg.pid ? { ...p, stars: msg.stars } : p
                        ),
                    };
                case WS_TYPES.TIMER_UPDATE:
                    return {
                        ...state,
                        widgets: { ...state.widgets, timer: msg.timer },
                        timeOffset: msg.serverNow ? msg.serverNow - Date.now() : state.timeOffset,
                    };
                case WS_TYPES.CAL_UPDATE:
                    return { ...state, widgets: { ...state.widgets, calendar: msg.calendar } };
                case WS_TYPES.NOTE_UPDATE:
                    return {
                        ...state,
                        widgets: { ...state.widgets, notes: { ...state.widgets.notes, [msg.kind]: msg.note } },
                    };
                case WS_TYPES.BG_SET:
                    return {
                        ...state,
                        widgets: { ...state.widgets, bg: { ...state.widgets.bg, [msg.board]: msg.bg } },
                    };
                case WS_TYPES.HAND_UPDATE:
                    return { ...state, hands: msg.hands || [] };
                case WS_TYPES.WHEEL_SPIN:
                    return { ...state, lastWheel: { pid: msg.pid, name: msg.name, durationMs: msg.durationMs, at: Date.now() } };
                case WS_TYPES.TEACHER_ONLINE:
                    return { ...state, teacherOnline: true };
                case WS_TYPES.TEACHER_OFFLINE:
                    return { ...state, teacherOnline: false };
                case WS_TYPES.SESSION_RESULTS: // teacher only
                    return { ...state, status: "ended", results: msg.results };
                case WS_TYPES.SESSION_ENDED:
                    return { ...state, status: "ended", leaderboard: msg.leaderboard || state.leaderboard };
                default:
                    return state;
            }
        }
        case "localGrade": {
            return {
                ...state,
                activeQuestions: state.activeQuestions.map((q) =>
                    q.qid === action.qid
                        ? {
                            ...q,
                            answers: (q.answers || []).map((a) =>
                                a.pid === action.pid ? { ...a, score: action.score } : a
                            ),
                        }
                        : q
                ),
            };
        }
        default:
            return state;
    }
}

const DRAW_EVENTS = [WS_TYPES.DRAW_OPS, WS_TYPES.DRAW_UNDO, WS_TYPES.DRAW_CLEAR, WS_TYPES.POINTER];

const useWhiteboardSocket = ({ wsUrl, token, enabled, onConnect }) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const boardsRef = useRef({}); // boardId -> ops[]
    const listenersRef = useRef(new Set());
    const wsRef = useRef(null);
    const retryRef = useRef({ attempt: 0, timer: null });
    const closedRef = useRef(false);
    const onConnectRef = useRef(onConnect);
    onConnectRef.current = onConnect;

    const emit = (msg) => {
        for (const listener of listenersRef.current) listener(msg);
    };

    const send = useCallback((msg) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(msg));
        }
    }, []);

    const subscribe = useCallback((listener) => {
        listenersRef.current.add(listener);
        return () => listenersRef.current.delete(listener);
    }, []);

    // add ops that originate OUTSIDE the canvas (e.g. image insertion):
    // append locally, repaint via the same event path, and send to the relay
    const injectDrawOps = useCallback((board, ops) => {
        const list = boardsRef.current[board] || (boardsRef.current[board] = []);
        list.push(...ops);
        for (const listener of listenersRef.current) {
            listener({ t: WS_TYPES.DRAW_OPS, board, ops });
        }
        send({ t: WS_TYPES.DRAW_OPS, board, ops });
    }, [send]);

    useEffect(() => {
        if (!enabled || !wsUrl || !token) return;
        closedRef.current = false;

        const connect = () => {
            if (closedRef.current) return;
            const separator = wsUrl.includes("?") ? "&" : "?";
            const ws = new WebSocket(`${wsUrl}${separator}token=${encodeURIComponent(token)}`);
            wsRef.current = ws;

            ws.onopen = () => {
                retryRef.current.attempt = 0;
                if (onConnectRef.current) onConnectRef.current(send);
            };

            ws.onmessage = (event) => {
                let msg;
                try {
                    msg = JSON.parse(event.data);
                } catch {
                    return;
                }

                if (msg.t === WS_TYPES.SNAPSHOT) {
                    boardsRef.current = msg.boards || {};
                    dispatch({ type: "snapshot", msg });
                    emit(msg);
                    return;
                }

                if (DRAW_EVENTS.includes(msg.t)) {
                    if (msg.t === WS_TYPES.DRAW_OPS) {
                        const ops = boardsRef.current[msg.board] || (boardsRef.current[msg.board] = []);
                        ops.push(...msg.ops);
                    } else if (msg.t === WS_TYPES.DRAW_UNDO) {
                        const ops = boardsRef.current[msg.board];
                        if (ops) {
                            boardsRef.current[msg.board] = ops.filter((op) => op.id !== msg.opId);
                        }
                    } else if (msg.t === WS_TYPES.DRAW_CLEAR) {
                        boardsRef.current[msg.board] = [];
                    }
                    emit(msg);
                    return;
                }

                dispatch({ type: "event", msg });
                emit(msg);

                if (msg.t === WS_TYPES.SESSION_ENDED || msg.t === WS_TYPES.SESSION_RESULTS) {
                    closedRef.current = true;
                }
                if (msg.t === WS_TYPES.ERROR && (msg.code === "ended" || msg.code === "full")) {
                    closedRef.current = true;
                    dispatch({ type: "status", status: "error", errorCode: msg.code });
                }
            };

            ws.onclose = () => {
                if (closedRef.current) return;
                dispatch({ type: "status", status: "reconnecting" });
                const attempt = ++retryRef.current.attempt;
                const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
                retryRef.current.timer = setTimeout(connect, delay);
            };

            ws.onerror = () => {
                ws.close();
            };
        };

        connect();

        return () => {
            closedRef.current = true;
            if (retryRef.current.timer) clearTimeout(retryRef.current.timer);
            if (wsRef.current) wsRef.current.close();
        };
    }, [enabled, wsUrl, token, send]);

    return { state, send, subscribe, boardsRef, injectDrawOps };
};

export default useWhiteboardSocket;
